/**
 * Volunteer create form.
 * Builds contact + establishments + engagement, then POSTs in one submit.
 */
import * as React from 'react';
import { User, BookOpen, Clock, Building2, Calendar, Mail, UserCog } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { Select } from '@/components/forms/Select';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import { volunteerCreateValidationSchema } from '@/utils/validation-schemas-utils';
import { createVolunteer } from '@/services/volunteers.service';
import { fetchRoles } from '@/services/roles.service';
import { getSubjects } from '@/services/subjects.service';
import { useEstablishmentsMultiselect } from '@/hooks/useEstablishmentsMultiselect';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { buildCreatableRoleOptions, getCreatableRoles } from '@/utils/interface-hierarchy-utils';
import type { CreateVolunteerDto, CreateVolunteerFormState } from '@/types/volunteer.types';
import type { UserWithProfile } from '@/types/user.types';
import { volunteerToUserWithProfile } from '@/utils/created-user-profile-utils';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import {
  areContactsIdentical,
  isPrimaryContactFormValid,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { useAuthStore } from '@/stores/authStore';
import { addDaysToDateString } from '@/utils/format-date-utils';
import {
  GENDER_OPTIONS,
  VOLUNTEER_FORM_QUERY_KEYS,
} from '@/components/volunteers/volunteer-configs';
import {
  applyCreateVolunteerField,
  buildCreateVolunteerDto,
  emptyCreateVolunteerForm,
  formatTargetHoursInput,
  getCreateVolunteerRolesHelperText,
  getEngagementEndOrderError,
  getEstablishmentHelperText,
  isCreateVolunteerSubmitBlocked,
  formatVolunteerMutationSuccess,
  mapVolunteerSubjectOptions,
  nextVolunteerContactState,
  parseTargetHoursInput,
  resolveCreateVolunteerRoleIds,
  resolveEffectiveManagedById,
  resolveVolunteerRoleLabel,
  shouldHideCoordinatorField,
  shouldTouchEngagementEndOnStartChange,
  type VolunteerContactType,
} from '@/components/volunteers/volunteer-form-utils';

/** Props for {@link CreateVolunteerView}. */
interface CreateVolunteerViewProps {
  onClose: () => void;
  /** Created user (UI profile) so the caller can add them to a session or matching immediately. */
  onSuccess?: (created: UserWithProfile) => void;
  initialRole?: string;
}

/** Volunteer create form. */
export const CreateVolunteerView: React.FC<Readonly<CreateVolunteerViewProps>> = ({
  onClose,
  onSuccess,
  initialRole,
}) => {
  const user = useAuthStore((s) => s.user);
  const authRoles = useAuthStore((s) => s.roles);
  const hideCoordinatorField = shouldHideCoordinatorField(authRoles);

  const validation = useFormValidation(volunteerCreateValidationSchema);
  const defaultCountry = useDefaultPhoneCountry();

  const { elements: volunteerElements = [] } = useInterfaceElements('volunteers');
  const availableRoles = React.useMemo(
    () => getCreatableRoles(volunteerElements),
    [volunteerElements],
  );
  const isSingleRoleMode = availableRoles.length <= 1;

  const [volunteerData, setVolunteerData] = React.useState<CreateVolunteerFormState>(() =>
    emptyCreateVolunteerForm(defaultCountry),
  );

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: volunteerData.contact,
    type: resolveContactCheckType(volunteerData.contactType),
    enabled: volunteerData.contactType !== 'unknown' && volunteerData.contact.trim().length > 0,
  });

  const contactExistsErrorKey =
    volunteerData.contactType === 'phone' ? 'phoneAlreadyExists' : 'emailAlreadyExists';
  const contactExistsError =
    contactExists === true ? getErrorMessage(contactExistsErrorKey) : undefined;

  const secondaryContactType = volunteerData.contactType === 'email' ? 'phone' : 'email';
  const { exists: secondaryContactExists, isChecking: isSecondaryChecking } = useContactExistsCheck(
    {
      value: volunteerData.secondaryContact,
      type: secondaryContactType,
      enabled:
        volunteerData.contactType !== 'unknown' && volunteerData.secondaryContact.trim().length > 0,
    },
  );

  const contactsAreIdentical = areContactsIdentical(
    volunteerData.contact,
    volunteerData.secondaryContact,
  );
  const secondaryContactExistsErrorKey =
    secondaryContactType === 'phone' ? 'phoneAlreadyExists' : 'emailAlreadyExists';
  const secondaryContactExistsError =
    secondaryContactExists === true ? getErrorMessage(secondaryContactExistsErrorKey) : undefined;

  const { data: roles = [] } = useQuery({
    queryKey: VOLUNTEER_FORM_QUERY_KEYS.roles,
    queryFn: fetchRoles,
  });

  React.useEffect(() => {
    validation.validateInitialFormData(volunteerData);
    // Mount-only: validate prefilled values (e.g. engagementStartDate = today) without blocking Create.
  }, []);

  /** Prefill role ids from `initialRole` or the single creatable role. */
  React.useEffect(() => {
    const newRoleIds = resolveCreateVolunteerRoleIds({
      initialRole,
      roles,
      availableRoles,
      isSingleRoleMode,
      currentRoleIdsLength: volunteerData.roleIds.length,
    });
    if (!newRoleIds) return;
    setVolunteerData((prev) => ({ ...prev, roleIds: newRoleIds }));
    validation.validateField('roleIds', newRoleIds, {
      ...volunteerData,
      roleIds: newRoleIds,
    });
  }, [initialRole, roles, isSingleRoleMode, availableRoles, volunteerData.roleIds.length]);

  const { data: subjects = [] } = useQuery({
    queryKey: VOLUNTEER_FORM_QUERY_KEYS.subjects,
    queryFn: async () => {
      const response = await getSubjects();
      return response.data;
    },
  });

  const effectiveManagedById = resolveEffectiveManagedById(
    hideCoordinatorField,
    user?.id,
    volunteerData.managedById,
  );

  const {
    isLoading: estabSearchLoading,
    onSearch: onEstabSearch,
    onLoadMore: onEstabLoadMore,
    hasMore: estabHasMore,
    getOptionsForValues: getEstabOptions,
  } = useEstablishmentsMultiselect();

  const {
    options: coordinatorOptions,
    isLoading: coordinatorLoading,
    onSearch: onCoordinatorSearch,
  } = useAdminAutocomplete({
    roleCodes: ['coordinator'],
    enabled: !hideCoordinatorField,
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateVolunteerDto) => createVolunteer(dto),
    onSuccess: (created: any) => {
      const roleLabel = resolveVolunteerRoleLabel(roles, volunteerData.roleIds[0]);
      toast.success(formatVolunteerMutationSuccess(roleLabel, 'create'));
      onSuccess?.(volunteerToUserWithProfile(created));
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Set one field and re-validate (contact uses `_contactType` context). */
  const handleFieldChange = (field: keyof CreateVolunteerFormState, value: unknown) => {
    setVolunteerData((prev) => applyCreateVolunteerField(prev, field, value));

    const updatedData = { ...volunteerData, [field]: value };

    if (field === 'contact') {
      validation.validateField(field, value, {
        ...updatedData,
        _contactType: volunteerData.contactType,
        _phoneCountryCode: volunteerData.contactCountryCode,
      });
    } else {
      validation.validateField(field, value, updatedData);
    }

    validation.markAsTouched(field);
    validation.markAsDirty(field);

    if (shouldTouchEngagementEndOnStartChange(field, value, updatedData.engagementEndDate)) {
      validation.markAsTouched('engagementEndDate');
    }
  };

  /** Changing contact type drops the secondary contact so we never keep an outdated email/phone. */
  const handleContactChange = (value: string, type: VolunteerContactType, countryCode?: string) => {
    setVolunteerData((prev) =>
      nextVolunteerContactState(prev, value, type, countryCode, defaultCountry),
    );

    validation.validateField('contact', value, {
      ...volunteerData,
      contact: value,
      _contactType: type,
      _phoneCountryCode: countryCode || volunteerData.contactCountryCode,
    });

    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  };

  /** Persist secondary contact and re-validate it. */
  const handleSecondaryContactChange = (value: string) => {
    setVolunteerData((prev) => ({ ...prev, secondaryContact: value }));

    validation.validateField('secondaryContact', value, {
      ...volunteerData,
      secondaryContact: value,
      _contactType: volunteerData.contactType,
    });

    validation.markAsTouched('secondaryContact');
    validation.markAsDirty('secondaryContact');
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST; blocked when engagement dates are inverted. */
  const handleSubmit = () => {
    const isValid = validation.validateForm({
      ...volunteerData,
      _contactType: volunteerData.contactType,
      _phoneCountryCode: volunteerData.contactCountryCode,
      _contactExists: contactExists,
    });
    if (isCreateVolunteerSubmitBlocked(isValid, volunteerData)) return;
    createMutation.mutate(buildCreateVolunteerDto(volunteerData, effectiveManagedById));
  };

  const subjectOptions = mapVolunteerSubjectOptions(subjects);
  const volunteerRoleOptions = React.useMemo(
    () => buildCreatableRoleOptions(availableRoles, roles),
    [availableRoles, roles],
  );
  const engagementEndOrderError = getEngagementEndOrderError(
    volunteerData.engagementStartDate,
    volunteerData.engagementEndDate,
  );

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un volontaire"
      subtitle="Renseignez les informations du volontaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={
        isPrimaryContactFormValid({
          primaryExists: contactExists,
          secondaryExists: secondaryContactExists,
          isPrimaryChecking,
          isSecondaryChecking,
          identical: contactsAreIdentical,
          formValid: validation.isFormValid(),
        }) && !engagementEndOrderError
      }
    >
      {!isSingleRoleMode && (
        <div className="form-section">
          <h4 className="form-section-title">
            <UserCog className="w-4 h-4" />
            Rôle(s)
          </h4>
          <MultiSelect
            label="Rôles"
            placeholder="Sélectionner les rôles"
            options={volunteerRoleOptions}
            value={volunteerData.roleIds}
            onChange={(values) => handleFieldChange('roleIds', values)}
            onBlur={() => handleBlur('roleIds')}
            helperText={getCreateVolunteerRolesHelperText(
              initialRole,
              volunteerData.roleIds.length > 0,
            )}
            required
            state={validation.getFieldState('roleIds')}
            error={validation.getFieldError('roleIds')}
          />
        </div>
      )}

      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Prénom"
            value={volunteerData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            placeholder="Ex: Marie"
            maxLength={100}
            required
            state={validation.getFieldState('firstName')}
            error={validation.getFieldError('firstName')}
          />
          <Input
            label="Nom"
            value={volunteerData.lastName}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            placeholder="Ex: Martin"
            maxLength={100}
            required
            state={validation.getFieldState('lastName')}
            error={validation.getFieldError('lastName')}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <Select
            label="Genre"
            value={volunteerData.gender}
            onChange={(value) => handleFieldChange('gender', value)}
            onBlur={() => handleBlur('gender')}
            options={GENDER_OPTIONS}
            placeholder="Sélectionner"
            required
            state={validation.getFieldState('gender')}
            error={validation.getFieldError('gender')}
          />
          <SmartContactInput
            label="Contact"
            value={volunteerData.contact}
            onContactChange={handleContactChange}
            onBlur={() => handleBlur('contact')}
            required
            state={contactExistsError ? 'error' : validation.getFieldState('contact')}
            error={contactExistsError ?? validation.getFieldError('contact')}
            helperText="Saisissez l'email ou le numéro de téléphone du VSC"
          />
          {volunteerData.contactType !== 'unknown' && (
            <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
              {volunteerData.contactType === 'email' ? (
                <PhoneInput
                  label="Téléphone (contact secondaire, optionnel)"
                  value={volunteerData.secondaryContact}
                  onChange={(e) => handleSecondaryContactChange(e.target.value)}
                  onBlur={() => handleBlur('secondaryContact')}
                  countryCode={volunteerData.secondaryContactCountryCode}
                  onCountryChange={(c) =>
                    setVolunteerData((p) => ({ ...p, secondaryContactCountryCode: c.code }))
                  }
                  state={
                    secondaryContactExistsError
                      ? 'error'
                      : validation.getFieldState('secondaryContact')
                  }
                  error={
                    secondaryContactExistsError ?? validation.getFieldError('secondaryContact')
                  }
                  helperText="Numéro secondaire pour joindre ce VSC"
                />
              ) : (
                <Input
                  type="text"
                  label="Email (contact secondaire, optionnel)"
                  value={volunteerData.secondaryContact}
                  onChange={(e) => handleSecondaryContactChange(e.target.value)}
                  onBlur={() => handleBlur('secondaryContact')}
                  icon={Mail}
                  placeholder="Ex: contact@example.com"
                  state={
                    secondaryContactExistsError
                      ? 'error'
                      : validation.getFieldState('secondaryContact')
                  }
                  error={
                    secondaryContactExistsError ?? validation.getFieldError('secondaryContact')
                  }
                  helperText="Email secondaire pour joindre ce VSC"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {!hideCoordinatorField && (
        <div className="form-section">
          <h4 className="form-section-title">
            <Building2 className="w-4 h-4" />
            Coordinateur
          </h4>
          <Autocomplete
            label="Coordinateur superviseur"
            placeholder="Sélectionner un coordinateur"
            options={coordinatorOptions}
            value={volunteerData.managedById}
            onChange={(value) => handleFieldChange('managedById', value)}
            onSearch={onCoordinatorSearch}
            isLoading={coordinatorLoading}
            minSearchLength={2}
            helperText="Le coordinateur qui supervisera ce VSC"
            state={validation.getFieldState('managedById')}
            error={validation.getFieldError('managedById')}
          />
        </div>
      )}

      <div className="form-section">
        <h4 className="form-section-title">
          <Building2 className="w-4 h-4" />
          Établissements
        </h4>
        <MultiSelect
          label="Établissements"
          placeholder="Rechercher un établissement..."
          options={getEstabOptions(volunteerData.establishmentIds)}
          value={volunteerData.establishmentIds}
          onChange={(values) => handleFieldChange('establishmentIds', values)}
          onBlur={() => handleBlur('establishmentIds')}
          onSearch={onEstabSearch}
          onLoadMore={onEstabLoadMore}
          hasMore={estabHasMore}
          isSearchLoading={estabSearchLoading}
          searchPlaceholder="Rechercher un établissement..."
          minSearchLength={2}
          maxVisibleTags={2}
          showSelectAll={false}
          required
          helperText={getEstablishmentHelperText(!!effectiveManagedById, ESTABLISHMENT_SEARCH_HINT)}
          state={validation.getFieldState('establishmentIds')}
          error={validation.getFieldError('establishmentIds')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Matières enseignées
        </h4>
        <MultiSelect
          label="Matières"
          placeholder="Sélectionnez les matières"
          options={subjectOptions}
          value={volunteerData.subjects}
          onChange={(values) => handleFieldChange('subjects', values)}
          onBlur={() => handleBlur('subjects')}
          maxVisibleTags={3}
          helperText="Sélectionnez les matières que le VSC peut enseigner"
          state={validation.getFieldState('subjects')}
          error={validation.getFieldError('subjects')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Clock className="w-4 h-4" />
          Disponibilités
        </h4>
        <AvailabilityGrid
          value={volunteerData.availability}
          onChange={(value) => handleFieldChange('availability', value)}
          disabled={false}
        />
        <p className="text-xs text-muted-foreground mt-2">
          Sélectionnez les créneaux de présence hebdomadaires du VSC
        </p>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Calendar className="w-4 h-4" />
          Dates d'engagement
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <DateSplitInput
            label="Date de début de mission"
            required
            value={volunteerData.engagementStartDate}
            onChange={(value) => handleFieldChange('engagementStartDate', value)}
            onBlur={() => handleBlur('engagementStartDate')}
            state={validation.getFieldState('engagementStartDate')}
            error={validation.getFieldError('engagementStartDate')}
          />
          <DateSplitInput
            label="Date de fin de contrat"
            required
            value={volunteerData.engagementEndDate}
            onChange={(value) => handleFieldChange('engagementEndDate', value)}
            onBlur={() => handleBlur('engagementEndDate')}
            minDate={addDaysToDateString(volunteerData.engagementStartDate, 1)}
            maxDate="2099-12-31"
            yearSortAsc
            state={
              engagementEndOrderError ? 'error' : validation.getFieldState('engagementEndDate')
            }
            error={engagementEndOrderError ?? validation.getFieldError('engagementEndDate')}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Définissez la période d'engagement du VSC
        </p>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <Input
            type="number"
            label="Nombre d'heures à réaliser"
            value={formatTargetHoursInput(volunteerData.targetHours)}
            onChange={(e) =>
              handleFieldChange('targetHours', parseTargetHoursInput(e.target.value))
            }
            onBlur={() => handleBlur('targetHours')}
            placeholder="Ex: 50"
            min={0}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
